import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/features/auth/contexts/auth.context';
import { phoneNumber, signIn } from '@/lib/auth-client';
import { clearOtpCache } from '@/services/otp';

interface UseOTPVerificationOptions {
  identifier: string;
  isEmail: boolean;
  onResend: () => void;
  /**
   * Optional callback called after successful OTP verification.
   * If not provided, defaults to redirecting to /dashboard
   */
  onSuccess?: () => void | Promise<void>;
}

export function useOTPVerification(options: UseOTPVerificationOptions) {
  const { identifier, isEmail, onResend, onSuccess } = options;
  const { t } = useTranslation();

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const { refetch: refetchSession } = useAuth();

  const handleSubmit = async (code: string) => {
    const sanitized = code.replace(/\D/g, '');
    if (sanitized.length !== 6) {
      setError(t('formErrors.otpLength'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (isEmail) {
        // signIn.emailOtp verifies the OTP and sets the session cookie
        const { error: signInError } = await signIn.emailOtp({
          email: identifier,
          otp: sanitized,
        });

        if (signInError) {
          const errorCode = signInError.code || 'INVALID_OTP';
          const errorMessage =
            t(`errors.${errorCode}`) !== `errors.${errorCode}`
              ? t(`errors.${errorCode}`)
              : t('formErrors.invalidOTP');
          throw new Error(errorMessage);
        }
      } else {
        const { error: verifyError } = await phoneNumber.verify({
          phoneNumber: identifier,
          code: sanitized,
        });

        if (verifyError) {
          const errorCode = verifyError.code || 'INVALID_OTP';
          const errorMessage =
            t(`errors.${errorCode}`) !== `errors.${errorCode}`
              ? t(`errors.${errorCode}`)
              : t('formErrors.invalidOTP');
          throw new Error(errorMessage);
        }
      }

      // Clear OTP cache after successful verification
      await clearOtpCache(identifier, isEmail);

      await refetchSession();

      // Call custom success handler or rely on route redirect when authenticated
      if (onSuccess) {
        await onSuccess();
      }
    } catch {
      setError(t('formErrors.invalidOTP'));
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (value: string) => {
    setCode(value);
    if (error) setError('');
  };

  const handleResend = async () => {
    try {
      await onResend();

      setCode('');

      // Show success toast
      const method = isEmail ? t('dashboard.auth.otp.byEmail') : t('dashboard.auth.otp.bySMS');
      toast.success(t('dashboard.auth.otp.resendSuccess', { method, identifier }), {
        duration: 4000,
      });
    } catch {
      setError(t('formErrors.resendFailed'));
    }
  };

  const handleManualSubmit = () => {
    handleSubmit(code);
  };

  return {
    code,
    handleChange,
    error,
    isSubmitting,
    handleResend,
    handleManualSubmit,
  };
}
